import { motion } from "framer-motion";
import { PrimaryButton } from "./PrimaryButton";
import { scrollToId, EASE } from "./motion";

const HERO_VIDEO =
  "https://customer-assets-jt897jd0.emergentagent.net/job_emergent-coffee-rsvp/artifacts/bpsano3l_51F64FD8-4687-44A4-A8C6-956CA7CABCD7.MP4";

const lineParent = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } } };
const lineChild = {
  hidden: { y: "115%" },
  show: { y: 0, transition: { duration: 0.95, ease: EASE } },
};

const Line = ({ children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span variants={lineChild} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section className="relative px-5 sm:px-8 lg:px-12 pt-16 sm:pt-20 pb-16 sm:pb-24 max-w-[1400px] mx-auto">
      {/* top strip */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="flex items-center justify-between text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-mushroom-deep mb-10 sm:mb-16"
        data-testid="hero-topbar"
      >
        <span>a gathering · bend, oregon</span>
        <span className="hidden sm:inline">rsvp — it's free</span>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end">
        {/* Headline */}
        <div className="lg:col-span-8">
          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-display font-black lowercase tracking-tighter text-ink text-[17vw] sm:text-[13vw] lg:text-[9.4vw] leading-[0.84]"
            data-testid="hero-headline"
          >
            <Line>coffee,</Line>
            <Line>create &amp;</Line>
            <Line>
              <span className="italic font-semibold text-butter">community</span>
            </Line>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-lg sm:text-2xl font-medium leading-[1.12] text-ink/85"
            data-testid="hero-lead"
          >
            an in-person gathering for women who are building, creating, or moving something forward.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.78, ease: EASE }}
            className="mt-4 font-display font-black leading-[0.95] text-2xl sm:text-3xl lg:text-4xl text-ink"
            data-testid="hero-location"
          >
            thump at the grove · bend, oregon
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-4 max-w-xl text-base sm:text-lg font-medium leading-[1.18] text-ink/60"
            data-testid="hero-subhead"
          >
            coffee, creativity, and community. a morning where you move your own work
            forward, alongside women doing the same.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
            className="mt-8"
          >
            <PrimaryButton testId="hero-save-spot" onClick={() => scrollToId("rsvp")}>
              save my spot
            </PrimaryButton>
            <p className="mt-3 text-sm font-semibold text-mushroom-deep" data-testid="hero-free-note">
              free to RSVP
            </p>
          </motion.div>
        </div>

        {/* Image — clipped tall arch frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
          className="lg:col-span-4"
        >
          <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[2rem] aspect-[3/4.2] shadow-xl bg-mushroom">
            <video
              src={HERO_VIDEO}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              data-testid="hero-video"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-ink/10 rounded-t-[999px] rounded-b-[2rem]" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
